import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  generationId: string;
  onSubmitSuccess: () => void;
}

export function FeedbackModal({
  open,
  onClose,
  generationId,
  onSubmitSuccess,
}: FeedbackModalProps) {
  const { t } = useTranslation();
  const [rating, setRating] = useState<'satisfied' | 'unsatisfied' | null>(null);
  const [comment, setComment] = useState('');
  const [requestRefund, setRequestRefund] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!rating) {
      setError(t('feedback.errors.selectRating', 'Please select a rating'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { getIdToken } = await import('@/lib/firebase');
      const token = await getIdToken();

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/generations/${generationId}/feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            rating,
            comment: comment.trim() || undefined,
            requestRefund: rating === 'unsatisfied' && requestRefund,
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || t('feedback.errors.submitFailed', 'Failed to submit feedback'));
      }

      onSubmitSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setError(err instanceof Error ? err.message : t('feedback.errors.submitFailed', 'Failed to submit feedback'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setRating(null);
      setComment('');
      setRequestRefund(false);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('feedback.title', 'How was the generation?')}</DialogTitle>
          <DialogDescription>
            {t('feedback.subtitle', 'Your feedback helps us improve the quality of generated images')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Rating Selection */}
          <div className="space-y-3">
            <Label>{t('feedback.ratingLabel', 'Rate this generation')}</Label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant={rating === 'satisfied' ? 'default' : 'outline'}
                className="h-auto py-4 flex flex-col items-center gap-2"
                onClick={() => {
                  setRating('satisfied');
                  setRequestRefund(false);
                }}
              >
                <ThumbsUp className="h-6 w-6" />
                <span>{t('feedback.satisfied', 'Satisfied')}</span>
              </Button>
              <Button
                type="button"
                variant={rating === 'unsatisfied' ? 'destructive' : 'outline'}
                className="h-auto py-4 flex flex-col items-center gap-2"
                onClick={() => setRating('unsatisfied')}
              >
                <ThumbsDown className="h-6 w-6" />
                <span>{t('feedback.unsatisfied', 'Not satisfied')}</span>
              </Button>
            </div>
          </div>

          {/* Comment (shown for unsatisfied) */}
          {rating === 'unsatisfied' && (
            <div className="space-y-2">
              <Label htmlFor="comment">
                {t('feedback.commentLabel', 'Tell us what went wrong (optional)')}
              </Label>
              <textarea
                id="comment"
                placeholder={t('feedback.commentPlaceholder', 'Example: The background doesn\'t match the product...')}
                value={comment}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
                rows={4}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
            </div>
          )}

          {/* Refund Request (shown for unsatisfied) */}
          {rating === 'unsatisfied' && (
            <div className="space-y-3">
              <div className="flex items-start space-x-3 rounded-lg border p-4">
                <input
                  type="checkbox"
                  id="requestRefund"
                  checked={requestRefund}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRequestRefund(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor="requestRefund"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {t('feedback.requestRefund', 'Request credit refund')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('feedback.refundPolicy', 'Refund requests are reviewed within 24 hours')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!rating || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('feedback.submitFeedback', 'Submit Feedback')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
